/**
 * DCTA-162 — the speaker the bank's sentences play through (spoken.md F31; `app/read-aloud.ts` decides what and when).
 *
 * One AudioContext for the page. iOS plays nothing a page starts on its own: `unlock()` runs inside the customer's own
 * tap (the microphone, Send, any tap in the chat) — the context is made and resumed there, and one silent frame is
 * played, after which later sentences may play without a tap. Web Audio, not an <audio> element: no lock-screen player
 * for the bank's words, and the iPhone's silent switch silences it — a phone set to silent stays silent.
 *
 * `play()` never throws and never waits forever: a context the browser keeps locked, audio that will not decode or a
 * sentence whose end never comes is silence (false, or let go after its length and a margin); an abort (Stop, the
 * microphone, the field, leaving the chat) stops the sentence at once.
 */
export interface AudioOut {
  /** Inside a user gesture: make later plays possible (iOS). Cheap and idempotent. */
  unlock(): void;
  /** One sentence. `started` when its sound begins. Resolves when it ends or is stopped (true), or at once when it cannot play (false). */
  play(audio: ArrayBuffer, signal: AbortSignal, started?: () => void): Promise<boolean>;
}

/** Past a sentence's own length, its end is not waited for any longer than this (ms). */
const END_MARGIN_MS = 1500;
/** A locked context asked to resume outside a tap: how long to wait for it before it counts as silence (ms). */
const RESUME_MS = 300;

type Ctor = new () => AudioContext;

/** `ctor`: a stand-in in tests; `null`: none; omitted: the browser's (webkit-prefixed on old Safari). */
export function webAudioOut(ctor?: Ctor | null): AudioOut | undefined {
  const Ctx: Ctor | undefined =
    ctor === null ? undefined : (ctor ?? globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: Ctor }).webkitAudioContext);
  if (Ctx === undefined) return undefined;
  let ctx: AudioContext | undefined;
  let unlocked = false;
  const context = (): AudioContext => (ctx ??= new Ctx());

  return {
    unlock() {
      const c = context();
      if (unlocked && c.state === "running") return;
      unlocked = true;
      void c.resume().catch(() => undefined);
      try {
        const frame = c.createBufferSource();
        frame.buffer = c.createBuffer(1, 1, 22050);
        frame.connect(c.destination);
        frame.start(0);
      } catch {
        /* a context that will not play even silence: later plays find it locked */
      }
    },

    async play(audio, signal, started) {
      if (signal.aborted) return true;
      const c = context();
      if (c.state !== "running") {
        await Promise.race([c.resume().catch(() => undefined), new Promise((resolve) => setTimeout(resolve, RESUME_MS))]);
        // Read again: resume() changed it, or the browser kept it locked.
        if ((c.state as string) !== "running") return false;
      }
      let buffer: AudioBuffer;
      try {
        buffer = await c.decodeAudioData(audio.slice(0));
      } catch {
        return false;
      }
      if (signal.aborted) return true;
      return new Promise<boolean>((resolve) => {
        const source = c.createBufferSource();
        source.buffer = buffer;
        source.connect(c.destination);
        let done = false;
        const quiet = (): void => {
          try {
            source.stop();
          } catch {
            /* already ended */
          }
        };
        const finish = (): void => {
          if (done) return;
          done = true;
          clearTimeout(watchdog);
          signal.removeEventListener("abort", onAbort);
          resolve(true);
        };
        const onAbort = (): void => {
          quiet();
          finish();
        };
        const watchdog = setTimeout(() => {
          quiet();
          finish();
        }, buffer.duration * 1000 + END_MARGIN_MS);
        source.onended = finish;
        signal.addEventListener("abort", onAbort, { once: true });
        source.start();
        started?.();
      });
    },
  };
}
